import './OrderBasket.scss';
import classnames from 'classnames';
import { RenderFormNodeComponent } from 'components/common-form/builder/form/FormBuilder';
import { DataApi, DataContext } from 'components/common-form/contexts/DataContext';
import { FieldMetadataContext } from 'components/common-form/contexts/FieldMetadataContext';
import { FormOrientationContext } from 'components/common-form/contexts/FormOrientationContext';
import { ProductViewContext } from 'components/common-form/contexts/ProductViewContext';
import { WithInvalidFieldsContext } from 'components/common-form/contexts/WithInvalidFieldsContext';
import { Checkbox } from 'components/shared/Checkbox/Checkbox';
import type { QuoteKeys, QuoteKeysPrefix } from 'locales/quoteKeys';
import type { FormRecord } from 'models/formError';
import { FC, useCallback, useMemo, useState } from 'react';
import { Collection } from 'state/utils/types';
import styled from 'styled-components';
import { getTableRowBackgroundColor } from 'utils/styling/tableRows';

import { connectOrdersBasketFormComponent } from './OrdersBasketForm.connect';
import type { OrdersBasketFormProps } from './OrdersBasketForm.models';
import { OrdersBasketStatusIcon } from './StatusIcon/OrdersBasketStatusIcon';

const StyledTr = styled.tr`
  .with-error-tooltip-component {
    & > .tooltip {
      width: 250px;
    }
  }
`;

export const OrdersBasketFormComponent: FC<OrdersBasketFormProps> = ({
  productView,
  formId,
  productDefinition,
  exposedStatus,
  isSelected,
  canBeSelected,
  errors,
  warnings,
  validationErrors,
  formJsonRepresentation,
  onCheckboxToggled,
  isVisible,
  formData,
  setFieldValue,
  fieldsMetadata,
  isNotEditable,
}) => {
  const data = useMemo(() => ({ ...formData }), [formData]);
  const dataApi: DataApi = useMemo(() => ({ setFieldValue }), [setFieldValue]);
  const dataContext = useMemo(() => [data as Collection<Readonly<unknown>>, dataApi] as const, [data, dataApi]);

  const [isHovered, setIsHovered] = useState(false);
  const onMouseEnter = useCallback(() => setIsHovered(true), []);
  const onMouseLeave = useCallback(() => setIsHovered(false), []);

  const bgColor = useMemo(() => getTableRowBackgroundColor(isSelected, isHovered), [isSelected, isHovered]);

  if (productDefinition === undefined) {
    return <span>Unexpected error</span>;
  }

  return (
    <StyledTr className={bgColor} data-e2e="orders-basket-form" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <td className="p-0">
        <div className="flex-center-x flex-center-y">
          <Checkbox
            e2e="orders-basket-select-checkbox"
            onChange={onCheckboxToggled}
            checked={isSelected}
            disabled={!canBeSelected}
            id={formId}
          />
        </div>
      </td>
      <td className="align-middle text-center">
        <OrdersBasketStatusIcon
          isVisible={isVisible}
          errors={errors}
          warnings={warnings}
          validationErrors={validationErrors}
          formJsonRepresentation={formJsonRepresentation}
          formId={formId}
          exposedStatus={exposedStatus}
        />
      </td>
      {productDefinition.children === undefined ? (
        <span>Unexpected error</span>
      ) : (
        <ProductViewContext.Provider value={productView}>
          <WithInvalidFieldsContext<FormRecord, QuoteKeys, QuoteKeysPrefix> validationErrors={validationErrors}>
            <FormOrientationContext.Provider value="grid">
              <FieldMetadataContext.Provider value={fieldsMetadata}>
                <DataContext.Provider value={dataContext}>
                  {productDefinition.children.map(productNode => (
                    <td
                      key={productNode.id}
                      style={{ minWidth: '150px' }}
                      className={classnames('', { 'order-basket--frozen': isNotEditable })}
                    >
                      {exposedStatus !== 'Loading' &&
                        (fieldsMetadata?.[productNode.id]?.isHidden ? (
                          <div className="field-hidden" />
                        ) : (
                          <RenderFormNodeComponent
                            tabId={formId}
                            level={1}
                            node={productNode}
                            quoteWorkspaceVersion={'Standard'}
                            batchWorkspaceVersion={'Standard'}
                            mustForceAdvancedMode={true}
                            workspaceType={'ordersBasket'}
                          />
                        ))}
                    </td>
                  ))}
                </DataContext.Provider>
              </FieldMetadataContext.Provider>
            </FormOrientationContext.Provider>
          </WithInvalidFieldsContext>
        </ProductViewContext.Provider>
      )}
    </StyledTr>
  );
};

export const OrdersBasketForm = connectOrdersBasketFormComponent(OrdersBasketFormComponent);
